import { envValue, type EnvRecord } from "./envPreflight";

/**
 * V5 CAMPAIGN. Three switches, all default OFF, both exactly "1" for on, unset,
 * empty or "0" off, anything else refused rather than guessed (CLAUDE.md
 * section 0). Turning either on is Kabir's.
 *
 * ACKRA_ENABLE_CAMPAIGN_PLAN: the weekly `campaign.plan` handler writes
 * proposals and ROI rows. Off, a queued run completes with a ran row saying
 * flag_off and writes nothing else. A proposal carries no copy and sends
 * nothing either way.
 *
 * ACKRA_ENABLE_MARKETING_OPTIN: a recorded marketing consent also sets
 * `threads.has_marketing_optin`, the fact the campaign sweep reads. Off, the
 * consent is recorded in `marketing_consents` and the thread flag is left
 * alone, so no wave can reach the contact. Its reader is
 * `recordMarketingConsent` (packages/db/src/marketingConsent.ts), which is
 * handed the environment and calls `planMarketingOptIn` itself.
 *
 * ACKRA_ENABLE_COMMENT_TRIGGERS: the API's ingest checks each comment against
 * the owner's comment triggers and writes `comment.trigger_matched`. Off, the
 * check does not run and `comment_triggers` is not read.
 */

export const ENABLE_CAMPAIGN_PLAN_ENV = "ACKRA_ENABLE_CAMPAIGN_PLAN";
export const ENABLE_MARKETING_OPTIN_ENV = "ACKRA_ENABLE_MARKETING_OPTIN";
export const ENABLE_COMMENT_TRIGGERS_ENV = "ACKRA_ENABLE_COMMENT_TRIGGERS";

function validateOneZero(raw: string, what: string): string | null {
  if (raw === "0" || raw === "1") return null;
  return `must be exactly "1" (${what} on) or "0" (off). Any other value is refused rather than read as on`;
}

function requested(env: EnvRecord, name: string): boolean {
  return envValue(env, name) !== undefined && env[name] === "1";
}

function plan(env: EnvRecord, name: string, what: string): boolean {
  if (envValue(env, name) === undefined) return false;
  const complaint = validateOneZero(env[name] ?? "", what);
  if (complaint !== null) throw new Error(`${name} ${complaint}.`);
  return env[name] === "1";
}

export const validateEnableCampaignPlan = (raw: string): string | null => validateOneZero(raw, "the campaign plan");
export const campaignPlanRequested = (env: EnvRecord): boolean => requested(env, ENABLE_CAMPAIGN_PLAN_ENV);
export const planCampaignPlan = (env: EnvRecord): boolean => plan(env, ENABLE_CAMPAIGN_PLAN_ENV, "the campaign plan");

export const validateEnableMarketingOptIn = (raw: string): string | null => validateOneZero(raw, "marketing opt-in");
export const marketingOptInRequested = (env: EnvRecord): boolean => requested(env, ENABLE_MARKETING_OPTIN_ENV);
export const planMarketingOptIn = (env: EnvRecord): boolean => plan(env, ENABLE_MARKETING_OPTIN_ENV, "marketing opt-in");

export const validateEnableCommentTriggers = (raw: string): string | null => validateOneZero(raw, "comment triggers");
export const commentTriggersRequested = (env: EnvRecord): boolean => requested(env, ENABLE_COMMENT_TRIGGERS_ENV);
export const planCommentTriggers = (env: EnvRecord): boolean => plan(env, ENABLE_COMMENT_TRIGGERS_ENV, "comment triggers");

/**
 * The shape of a consent's `evidence_ref`: an id of the evidence (an event id,
 * a platform message id, an owner note id), never prose. The same shape as the
 * catalog's `ID` (eventCatalog.ts), so a customer's words cannot ride into
 * `marketing_consents` as a "reference" (CLAUDE.md sections 6 and 10).
 */
const EVIDENCE_REF = /^[A-Za-z0-9_.:-]{1,128}$/;
export const isEvidenceRef = (v: string): boolean => EVIDENCE_REF.test(v);
